import { useEffect, useRef } from 'react'
import { useTheme } from '../context/ThemeContext.js'
import { usePageMeta } from '../hooks/ui/usePageMeta.js'
import { useFixerContent } from '../hooks/i18n/usePageContent.js'
import { LocaleToggle } from './ui/LocaleToggle.js'
import { useFixerHeroScrollParallax } from '../hooks/ui/useFixerHeroScrollParallax.js'
import { useHeroTilt } from '../hooks/ui/useHeroTilt.js'
import { FixerAsciiRainLayer } from './FixerAsciiRain.js'
import { FixerContactSection } from './FixerContactSection.js'
import { FixerFaqSection } from './FixerFaqSection.js'
import { FixerHeroHighlights } from './FixerHeroHighlights.js'
import { FixerHeroVisual } from './FixerHeroVisual.js'
import { FixerMobileNav } from './FixerMobileNav.js'
import { FixerProductionSection } from './FixerProductionSection.js'
import { FixerServicesSection } from './FixerServicesSection.js'
import { FixerShowcaseSection } from './FixerShowcaseSection.js'
import {
  FixerBulletList,
  FixerCard,
  FixerCtaRow,
  FixerKicker,
  FixerSection,
  FixerSectionIntro,
} from './fixer-ui.js'
import { FutureDotMatrixIcon, FutureDotButton, FutureStatusPill } from './future-chat/FutureDotPrimitives.js'
import {
  FIXER_BRIEF_FORM_HASH,
  buildFixerStructuredData,
  PAGE_OG_IMAGE,
  PAGE_URL,
  phone,
  phoneTel,
} from '../data/fixer-content.js'
import './future-chat/future-chat-theme.css'
import './fixer-page.css'

interface FixerPageProps {
  onBack: () => void
}

function CtaArrow() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  )
}

function FixerPage({ onBack }: FixerPageProps) {
  const { isDarkMode, toggleTheme } = useTheme()
  const theme = isDarkMode ? 'dark' : 'light'

  const scrollRootRef = useRef<HTMLDivElement>(null)
  const heroPanelRef = useRef<HTMLDivElement>(null)

  const {
    stageStyle,
    imageStyle,
    onPointerMove,
    onPointerLeave,
    reducedMotion: tiltReducedMotion,
  } = useHeroTilt({
    maxDeg: 4,
    parallaxPx: 20,
    containerRef: heroPanelRef,
  })

  const { scrollLayerStyle, reducedMotion: scrollReducedMotion } = useFixerHeroScrollParallax(
    scrollRootRef,
    heroPanelRef
  )

  const reducedMotion = tiltReducedMotion || scrollReducedMotion

  const c = useFixerContent()
  const canonical = PAGE_URL
  const year = new Date().getFullYear()

  usePageMeta(c.meta.title, c.meta.description, canonical, {
    ogTitle: c.meta.title,
    ogDesc: c.meta.description,
    ogImage: PAGE_OG_IMAGE,
    ogUrl: canonical,
    keywords: c.meta.keywords,
    noindex: true,
  })

  useEffect(() => {
    const existing = document.getElementById('fixer-structured-data')
    if (existing) existing.remove()

    const script = document.createElement('script')
    script.type = 'application/ld+json'
    script.id = 'fixer-structured-data'
    script.textContent = JSON.stringify(buildFixerStructuredData())
    document.head.appendChild(script)

    return () => {
      const s = document.getElementById('fixer-structured-data')
      if (s) s.remove()
    }
  }, [])

  return (
    <div
      data-future-chat-theme={theme}
      className="future-chat-shell fixer-page-shell h-[100dvh] overflow-hidden"
    >
      <FixerAsciiRainLayer
        scrollRootRef={scrollRootRef}
        heroPanelRef={heroPanelRef}
        reducedMotion={reducedMotion}
        isDarkMode={isDarkMode}
      />

      <div ref={scrollRootRef} className="fixer-page-scroll custom-scrollbar">
        <header className="fixer-page-header">
          <div className="flex min-w-0 items-center gap-3">
            <button type="button" onClick={onBack} className="fixer-btn-ghost hidden sm:inline-flex">
              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              F.B/c
            </button>
            <span className="future-brand-lockup-text text-base sm:text-lg">{c.hero.brandLockup}</span>
          </div>

          <nav className="fixer-page-header-nav" aria-label={c.chrome.sectionsAria}>
            {c.nav.headerLinks.map(link => (
              <a key={link.href} href={link.href}>{link.label}</a>
            ))}
          </nav>

          <div className="fixer-page-header-actions flex shrink-0 items-center gap-1">
            <FixerMobileNav
              links={c.nav.links}
              ctaHref={FIXER_BRIEF_FORM_HASH}
              ctaLabel={c.hero.sendBriefLabel}
            />
            <LocaleToggle className="future-locale-toggle fixer-locale-toggle" />
            <FutureDotButton
              glyph={theme === 'dark' ? 'sun' : 'moon'}
              label={theme === 'dark' ? c.chrome.lightMode : c.chrome.darkMode}
              onClick={toggleTheme}
            />
            <a href={FIXER_BRIEF_FORM_HASH} className="fixer-btn-primary !py-2 !px-4 text-[11px] hidden sm:inline-flex">
              {c.hero.sendBriefLabel}
              <CtaArrow />
            </a>
          </div>
        </header>

        <main>
          <section
            ref={heroPanelRef}
            onPointerMove={onPointerMove}
            onPointerLeave={onPointerLeave}
            className="fixer-hero-panel fixer-hero-band"
            aria-labelledby="fixer-hero-heading"
          >
            <div className="absolute inset-0">
              <FixerHeroVisual
                stageStyle={stageStyle}
                imageStyle={imageStyle}
                scrollLayerStyle={scrollLayerStyle}
                reducedMotion={reducedMotion}
              />
            </div>

            <div className="fixer-hero-inner">
              <div className="flex flex-1 flex-col gap-10 lg:flex-row lg:items-center lg:justify-between lg:gap-12">
                <div className="fixer-hero-copy flex max-w-xl flex-col gap-4">
                  <FixerKicker channel="signal" className="text-white/80">
                    {c.hero.overline}
                  </FixerKicker>
                  <h1
                    id="fixer-hero-heading"
                    className="font-display text-4xl font-bold tracking-tight text-white md:text-5xl lg:text-[3.25rem] lg:leading-[1.08]"
                  >
                    {c.hero.title}
                  </h1>
                  <p className="fixer-hero-copy-lead max-w-lg text-base leading-relaxed text-white/90 md:text-lg">
                    {c.hero.subtitle}
                  </p>

                  <FixerCtaRow>
                    <a href={FIXER_BRIEF_FORM_HASH} className="fixer-btn-primary w-full sm:w-auto">
                      {c.hero.sendBriefLabel}
                      <CtaArrow />
                    </a>
                    <a href={phoneTel} className="fixer-btn-secondary w-full sm:w-auto">
                      {c.hero.callLabel} · {phone}
                    </a>
                    <a href="#showcase" className="fixer-btn-secondary w-full sm:w-auto">
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                      {c.hero.viewWorkLabel}
                    </a>
                  </FixerCtaRow>

                  <p className="flex items-center gap-2 text-sm text-white/65">
                    <FutureDotMatrixIcon glyph="location" state="signal" size={14} className="shrink-0" />
                    {c.hero.locationLine}
                  </p>
                </div>

                <FixerHeroHighlights />
              </div>
            </div>
          </section>

          <FixerSection>
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_minmax(240px,320px)] lg:gap-12">
              <div className="fixer-stack-lg">
                <p className="fixer-body max-w-2xl">{c.hero.body}</p>
                <p className="fixer-body max-w-2xl">{c.hero.background}</p>
                <p className="fixer-body max-w-2xl text-[var(--fc-text)]">{c.hero.ctaPrompt}</p>
                <FixerCtaRow>
                  <a href={FIXER_BRIEF_FORM_HASH} className="fixer-btn-primary w-full sm:w-auto">
                    {c.hero.sendBriefLabel}
                  </a>
                  <a href={phoneTel} className="fixer-btn-secondary w-full sm:w-auto">
                    {c.hero.callLabel}
                  </a>
                </FixerCtaRow>
              </div>

              <FixerCard signalRail={false}>
                <div className="fixer-stack-md">
                  <FixerKicker channel="memory">{c.chrome.localContext}</FixerKicker>
                  <p className="fixer-subheading">{c.hero.asideTitle}</p>
                  <p className="fixer-body-sm">{c.hero.asideBody}</p>
                </div>
              </FixerCard>
            </div>
          </FixerSection>

          <FixerSection className="!py-10">
            <div className="fixer-trust-strip">
              {c.chrome.trustStrip.map(stat => (
                <div key={stat.label} className="fixer-trust-cell">
                  <FixerKicker channel="signal" active={false}>{stat.label}</FixerKicker>
                  <span className="fixer-subheading">{stat.value}</span>
                </div>
              ))}
            </div>
          </FixerSection>

          <FixerSection className="fixer-wedge-band !py-10">
            <div className="fixer-wedge-inner">
              <FixerKicker channel="signal">{c.internationalWedge.kicker}</FixerKicker>
              <h2 className="fixer-heading max-w-2xl">{c.internationalWedge.title}</h2>
              <p className="fixer-body max-w-3xl">{c.internationalWedge.copy}</p>
            </div>
          </FixerSection>

          <FixerServicesSection />

          <FixerShowcaseSection />

          <FixerProductionSection />

          <FixerSection id="for">
            <div className="fixer-stack-xl">
              <FixerSectionIntro
                channel="screen"
                kicker={c.forSection.number}
                title={c.forSection.title}
                titleId="fixer-for-heading"
              />
              <div className="fixer-card-grid-2">
                <FixerCard>
                  <div className="fixer-stack-md">
                    <h3 className="fixer-subheading">{c.chrome.typicalNeedsTitle}</h3>
                    <FixerBulletList items={c.typicalNeeds} />
                  </div>
                </FixerCard>
                <FixerCard>
                  <div className="fixer-stack-md">
                    <h3 className="fixer-subheading">{c.chrome.whoTitle}</h3>
                    <FixerBulletList items={c.whoThisIsFor} />
                  </div>
                </FixerCard>
              </div>
              <FixerCard>
                <div className="fixer-stack-md">
                  <h3 className="fixer-subheading">{c.chrome.productionTypesTitle}</h3>
                  <FixerBulletList items={c.productionTypes} />
                </div>
              </FixerCard>
            </div>
          </FixerSection>

          <FixerSection id="process">
            <div className="fixer-stack-xl">
              <FixerSectionIntro
                channel="report"
                kicker={c.processIntro.number}
                title={c.processIntro.title}
                copy={c.processIntro.copy}
                titleId="fixer-process-heading"
              />
              <div className="fixer-process-grid" role="list">
                {c.processSteps.map(step => (
                  <FixerCard key={step.num} className="flex flex-col gap-3" interactive role="listitem">
                    <span className="fixer-step-badge">{step.num}</span>
                    <h3 className="fixer-subheading">{step.title}</h3>
                    <p className="fixer-body-sm">{step.description}</p>
                  </FixerCard>
                ))}
              </div>
            </div>
          </FixerSection>

          <FixerSection id="coverage">
            <div className="fixer-stack-xl">
              <FixerSectionIntro
                channel="vision"
                kicker={c.coverage.number}
                title={c.coverage.title}
                copy={c.coverage.copy}
                titleId="fixer-coverage-heading"
              />
              <div className="flex flex-wrap gap-2" aria-label={c.chrome.coverageAria}>
                {c.coverageAreas.map(area => (
                  <FutureStatusPill key={area} glyph="location" active={false}>
                    {area}
                  </FutureStatusPill>
                ))}
              </div>
            </div>
          </FixerSection>

          <FixerFaqSection />

          <FixerContactSection year={year} />
        </main>
      </div>
    </div>
  )
}

export default FixerPage
